import { Formik, FormikHelpers, FormikProps } from "formik";
import _, { cloneDeep } from "lodash";
import moment from "moment";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  EventReportInfo,
  SubmitEventReport,
} from "../../../generated/data-contracts";
import {
  AvisUtils,
  ErrorUtils,
  IAxiosError,
  LicenseesUrls,
  LicenseesUtils,
  api,
  prepareCollectionForPost,
  nullsToUndefined,
} from "../../../utils";
import { AvisLoader } from "../../shared/components";
import {
  NotPortalReportMessage,
  SaveAndSubmit,
} from "../../shared/components/form";
import { AvisConfirmModalShort } from "../../shared/components/modal";
import { AvisContext } from "../../shared/context/AvisContext";
import { LicenseReportForm } from "./components/LicenseReportForm";
import { LicenseReportHeader } from "./components/LicenseReportHeader";
import { ReportClientEventsForm } from "./components/ReportForms";
import { validationSchemaClientEvents } from "./validation/validation";
import { AvisConstants } from "../../../data/avisConstants";

export function LicenseReportPageEvents() {
  const { res } = useContext(AvisContext);
  const [error, setError] = useState<IAxiosError>();
  const [firstLoading, setFirstLoading] = useState<boolean>(true);
  const [loadingBriefInfo, setLoadingBriefInfo] = useState<boolean>(true);
  const [licenseReport, setLicenseReport] = useState<
    EventReportInfo | undefined
  >();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [redirectToRowIndex, setRedirectToRowIndex] = useState<
    number | undefined
  >();
  const navigate = useNavigate();
  const navigateToProgramReport = useCallback(
    (licenseClientEventId?: number) => {
      const urlParams = LicenseesUtils.getUrlParams();
      const urlToProgramReport = LicenseesUtils.getUrlWithParams(
        LicenseesUrls.licenseeLicenseViewReportPP,
        {
          licenseId: urlParams.licenseId,
          licenseInvoicePlanId: urlParams.licenseInvoicePlanId,
          licenseReportFormType: urlParams.licenseReportFormType,
          licenseClientEventId: licenseClientEventId,
        },
      );
      navigate(urlToProgramReport);
    },
    [navigate],
  );

  useEffect(() => {
    if (
      !licenseReport ||
      isSubmitting ||
      typeof redirectToRowIndex === "undefined" ||
      !licenseReport.rows?.[redirectToRowIndex]?.licenseClientEventId
    )
      return;

    navigateToProgramReport(
      licenseReport.rows[redirectToRowIndex].licenseClientEventId,
    );
  }, [
    isSubmitting,
    licenseReport,
    navigateToProgramReport,
    redirectToRowIndex,
  ]);

  const loadReportData = useCallback(
    (formikHelper?: FormikHelpers<SubmitEventReport>) => {
      if (isSubmitting) return;

      const urlParams = LicenseesUtils.getUrlParams();
      if (!urlParams.licenseId || !urlParams.licenseInvoicePlanId) return;

      setLoadingBriefInfo(true);
      api
        .licenseesGetEventReport(
          urlParams.licenseId,
          urlParams.licenseInvoicePlanId,
        )
        .then((res) => {
          const dataNormalized = nullsToUndefined<EventReportInfo>(res.data);

          setLicenseReport(dataNormalized);
          if (formikHelper) {
            const submitDataObject = {
              ...dataNormalized,
              submitReport: false,
            };
            formikHelper.resetForm({
              values: submitDataObject as SubmitEventReport,
            });
          }
        })
        .catch(setError)
        .finally(() => {
          setLoadingBriefInfo(false);
          setFirstLoading(false);
        });
    },
    [isSubmitting],
  );
  useEffect(() => {
    if (firstLoading) loadReportData();
  }, [firstLoading, loadReportData]);

  const hasNoPortalReport = useMemo(
    () =>
      LicenseesUtils.hasNoPortalReport(
        licenseReport?.portalReportStatusType,
        licenseReport?.eventNotOccuredPortal,
        licenseReport?.rows?.length,
      ),
    [licenseReport],
  );

  const handleSubmit = (
    formikValues: SubmitEventReport,
    formikHelpers: FormikHelpers<SubmitEventReport>,
  ) => {
    if (!licenseReport) return;
    setIsSubmitting(true);

    const values = _.cloneDeep(formikValues);
    if (values.eventNotOccuredPortal) {
      //Empty bag rows with possible incomplete data
      values.rows = [];
    } else {
      prepareCollectionForPost(values, "rows");
    }

    api
      .licenseesPostEventReport(
        licenseReport.licenseHeaderId,
        licenseReport.licenseInvoicePlanId,
        values,
      )
      .then(() => {
        ErrorUtils.showSuccessfulMessage(
          res("message_Success"),
          values.submitReport
            ? res("licensees_LicenseReports_notification_Submit")
            : res("licensees_LicenseReports_notification_Save"),
          500,
        );

        if (values.submitReport) setSubmitted(true);
        loadReportData(formikHelpers);
      })
      .catch((err) => ErrorUtils.handleFormValidation(err, formikHelpers))
      .finally(() => {
        setIsSubmitting(false);
        setIsModalOpen(false);
      });
  };

  const renderActionButtons = useCallback(
    (editAllowed: boolean, formikProps: FormikProps<SubmitEventReport>) => {
      if (!editAllowed || submitted) return;

      return (
        <SaveAndSubmit
          formikProps={formikProps}
          disabled={isSubmitting}
          setIsModalOpen={setIsModalOpen}
        />
      );
    },
    [isSubmitting, submitted],
  );

  const renderForm = useCallback(
    (formikProps: FormikProps<SubmitEventReport>) => {
      const editAllowed = AvisUtils.isEditablePortalReportStatusTypeInfo(
        licenseReport?.portalReportStatusType,
      );
      if (loadingBriefInfo && firstLoading) return <AvisLoader inline />;

      if (hasNoPortalReport) return <NotPortalReportMessage />;

      return (
        <>
          <AvisConfirmModalShort
            isOpen={isModalOpen}
            content={res("licensees_LicenseReports_notification_ConfirmSubmit")}
            confirm={formikProps.submitForm}
            cancel={() => setIsModalOpen(false)}
            title={res(
              "licensees_LicenseReports_notification_ConfirmSubmitTitle",
            )}
            disableControls={isSubmitting}
          />
          <ReportClientEventsForm
            bag={formikProps}
            bagField="rows"
            periodFrom={moment(licenseReport?.periodFrom).toDate()}
            periodTo={moment(licenseReport?.periodTo).toDate()}
            disabled={!editAllowed || submitted}
            loading={isSubmitting}
            allowBlankReport={
              licenseReport?.licenseSettingsInfo?.allowBlankReport
            }
            setRedirectToRowIndex={setRedirectToRowIndex}
            navigateToProgramReport={navigateToProgramReport}
          />
          {renderActionButtons(editAllowed, formikProps)}
        </>
      );
    },
    [
      licenseReport,
      loadingBriefInfo,
      firstLoading,
      hasNoPortalReport,
      isModalOpen,
      res,
      isSubmitting,
      submitted,
      navigateToProgramReport,
      renderActionButtons,
    ],
  );

  if (!licenseReport || redirectToRowIndex) return <AvisLoader />;

  return (
    <LicenseReportForm
      data={cloneDeep(licenseReport) as EventReportInfo}
      error={error}
      loading={loadingBriefInfo && firstLoading}
      backLink={{
        title: res("label_Back"),
        //specific navigation after copying pp (fix #153760)
        forceNavigateToUrlParams: [
          AvisConstants.licenses_reports_pp_copy_param,
        ],
        url: LicenseesUtils.getUrlWithParams(
          LicenseesUrls.licenseeReports,
          {},
          true,
        ),
      }}
    >
      <LicenseReportHeader data={licenseReport} />

      <Formik<SubmitEventReport>
        initialValues={{
          eventNotOccuredPortal: licenseReport?.eventNotOccuredPortal,
          submitReport: false,
          rows: licenseReport?.rows ?? [],
        }}
        onSubmit={handleSubmit}
        enableReinitialize
        validationSchema={validationSchemaClientEvents(
          res,
          licenseReport?.periodFrom
            ? moment(licenseReport.periodFrom).toDate()
            : undefined,
          licenseReport?.periodTo
            ? moment(licenseReport.periodTo).toDate()
            : undefined,
        )}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {renderForm}
      </Formik>
      {isSubmitting && <AvisLoader />}
    </LicenseReportForm>
  );
}
